import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GridReadyEvent } from '@ag-grid-community/core';
import { AgFns, BaseService, GridOptionsExt, GuidelineOverrideGrid, ISortModel, SaveAndQueryComponent } from '@core';
import { Account } from '@models';
import { UtilFns } from '@utils';

import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';

/**
 * AI Guidelines of one account - Account Manager > Program Preparation. GlobalAdmins only.
 *
 * One row per master. This page writes no new rules: it can only override a master for this
 * account, once. An account override applies to every program of the account; a program can
 * override it again on the Program form. The page shows those program overrides, and warns on save
 * when a changed account override has program overrides under it.
 */
@Component({
  selector: 'prox-account-guidelines',
  templateUrl: './account-guidelines.component.html',
  standalone: false,
})
export class AccountGuidelinesComponent extends SaveAndQueryComponent {
  accountId!: string;
  account!: Account;
  gridOptions: GridOptionsExt = {};
  grid: GuidelineOverrideGrid;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
    this.grid = new GuidelineOverrideGrid(
      this.dbSaveService,
      () => ({ level: 'account', id: this.accountId }),
      () => this.isReadOnly(),
    );
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (params) => params.data.master.id,
    });
    this.grid.setGuidelines(await this.dbQueryService.getGuidelinesForAccount(this.accountId));

    this.setTitle('AI Guidelines');
    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const sortModel: ISortModel = [{ colId: 'master', sort: 'asc' }];
    AgFns.initGrid(event, this.grid.colDefs(), sortModel);
  }

  override async beforeSave() {
    const problems = this.grid.problems();
    if (problems.length > 0) {
      await this.dialogService.okDialog('Cannot save', problems.join('<br/>'));
      return false;
    }
    const warnings = this.grid.warnings();
    if (warnings.length > 0) {
      await this.dialogService.okDialog('Check the program overrides', warnings.join('<br/>'));
    }
    return true;
  }

  override async afterSave() {
    this.gridOptions.apiX?.refreshCells({ force: true });
  }

  override async afterUndo() {
    this.gridOptions.apiX?.refreshCells({ force: true });
  }

  isReadOnly() {
    return !this.authUser?.isGlobalAdmin;
  }
}
